import { useEffect, useMemo, useRef, useState } from "react";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useAccounting, previewCode, TIPO_LABEL, TIPO_CLASS } from "../lib/accounting";
import { Landmark } from "lucide-react";

/** Header: Cliente → Contrato → Frente. value = {client_code, contract_code, frente_code}; onChange(patch). */
export function CostCodeHeader({ value, onChange, testid = "cc", disabled }) {
  const { clients, contracts, settings } = useAccounting();
  const activeClients = clients.filter((c) => c.active !== false);
  const ctList = contracts.filter((c) => c.active !== false && (!value.client_code || c.client_code === value.client_code));
  const contract = contracts.find((c) => c.code === value.contract_code);
  const req = settings?.required;
  return (
    <div className="grid grid-cols-3 gap-2 border border-dashed p-2 bg-muted/20" data-testid={`${testid}-header`}>
      <div className="col-span-3 flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-primary">
        <Landmark size={10} /> Trazabilidad contable {req && <span className="text-destructive">· obligatoria</span>}
      </div>
      <div className="space-y-1">
        <Label className="text-[9px] font-bold uppercase tracking-widest">Cliente</Label>
        <Select value={value.client_code || "_none"} disabled={disabled}
          onValueChange={(v) => onChange({ client_code: v === "_none" ? "" : v, contract_code: "", frente_code: "" })}>
          <SelectTrigger className="rounded-none h-7 text-[11px]" data-testid={`${testid}-client`}><SelectValue placeholder="Cliente" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_none">— Sin cliente —</SelectItem>
            {activeClients.map((c) => <SelectItem key={c.code} value={c.code}>{c.code} · {c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label className="text-[9px] font-bold uppercase tracking-widest">Contrato</Label>
        <Select value={value.contract_code || "_none"} disabled={disabled || !value.client_code}
          onValueChange={(v) => onChange({ contract_code: v === "_none" ? "" : v, frente_code: "" })}>
          <SelectTrigger className="rounded-none h-7 text-[11px]" data-testid={`${testid}-contract`}><SelectValue placeholder="Contrato" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_none">— Sin contrato —</SelectItem>
            {ctList.map((c) => <SelectItem key={c.code} value={c.code}>{c.code} · {c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label className="text-[9px] font-bold uppercase tracking-widest">Frente</Label>
        <Select value={value.frente_code || "_none"} disabled={disabled || !contract}
          onValueChange={(v) => onChange({ frente_code: v === "_none" ? "" : v })}>
          <SelectTrigger className="rounded-none h-7 text-[11px]" data-testid={`${testid}-frente`}><SelectValue placeholder="Frente" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_none">— General —</SelectItem>
            {(contract?.frentes || []).map((f) => <SelectItem key={f.code} value={f.code}>{f.code} · {f.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

/** Per-line: subcuenta typeahead + tipo (when D/I) + preview of full code.
 *  value = {subcuenta, tipo, depto}; onChange(patch). */
export function AccountPicker({ value, onChange, header, equipmentCode, deptoCode, testid = "cc-line", disabled }) {
  const { accounts } = useAccounting();
  const v = value || {};
  const account = accounts.find((a) => a.subcuenta === v.subcuenta);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  const results = useMemo(() => {
    const n = q.trim().toLowerCase();
    return accounts.filter((a) => a.active !== false && (!n || `${a.subcuenta} ${a.concepto} ${a.cuenta_name}`.toLowerCase().includes(n))).slice(0, 12);
  }, [accounts, q]);
  const tipos = account ? account.tipo.split("/") : [];
  const code = previewCode({ header, line: v, account, equipmentCode, deptoCode });
  const needsEq = account?.requires_equipment && !equipmentCode;

  return (
    <div className="flex flex-wrap items-center gap-1.5" ref={boxRef} data-testid={testid}>
      <div className="relative flex-1 min-w-[220px]">
        {account && !open ? (
          <button type="button" disabled={disabled} onClick={() => { setQ(""); setOpen(true); }}
            className="w-full h-7 border bg-background px-2 text-left text-[11px] flex items-center gap-1.5 truncate"
            data-testid={`${testid}-selected`}>
            <span className={`px-1 text-[9px] font-black ${TIPO_CLASS[tipos.length === 1 ? tipos[0] : (v.tipo || "D")] || "bg-slate-400 text-white"}`}>{account.tipo}</span>
            <span className="font-bold">{account.subcuenta}</span>
            <span className="truncate text-muted-foreground">{account.concepto}</span>
          </button>
        ) : (
          <input value={q} disabled={disabled} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
            placeholder="Subcuenta: 6101 Diésel, 6402 Preventivo…" data-testid={`${testid}-input`}
            className="w-full h-7 border bg-background px-2 text-[11px] focus:outline-none focus:ring-1 focus:ring-primary" />
        )}
        {open && !disabled && (
          <div className="absolute z-50 left-0 right-0 mt-0.5 max-h-56 overflow-y-auto border bg-popover shadow-lg" data-testid={`${testid}-results`}>
            {v.subcuenta && (
              <button type="button" className="w-full text-left px-2 py-1 text-[11px] text-destructive hover:bg-muted"
                onClick={() => { onChange({ subcuenta: "", tipo: "" }); setOpen(false); }} data-testid={`${testid}-clear`}>Quitar cuenta</button>
            )}
            {results.map((a) => (
              <button type="button" key={a.subcuenta} data-testid={`${testid}-opt-${a.subcuenta}`}
                onClick={() => { onChange({ subcuenta: a.subcuenta, tipo: a.tipo.includes("/") ? "" : a.tipo }); setOpen(false); setQ(""); }}
                className="w-full text-left px-2 py-1 text-[11px] hover:bg-muted flex items-center gap-1.5">
                <span className={`px-1 text-[9px] font-black ${TIPO_CLASS[a.tipo.split("/")[0]] || ""}`}>{a.tipo}</span>
                <span className="font-bold w-9">{a.subcuenta}</span>
                <span className="truncate">{a.concepto}</span>
                <span className="ml-auto text-[9px] text-muted-foreground truncate max-w-[120px]">{a.cuenta_name}</span>
              </button>
            ))}
            {results.length === 0 && <div className="px-2 py-1 text-[11px] text-muted-foreground">Sin coincidencias</div>}
          </div>
        )}
      </div>
      {tipos.length > 1 && (
        <Select value={v.tipo || "_none"} onValueChange={(t) => onChange({ tipo: t === "_none" ? "" : t })} disabled={disabled}>
          <SelectTrigger className="rounded-none h-7 text-[11px] w-28" data-testid={`${testid}-tipo`}><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_none">Tipo…</SelectItem>
            {tipos.map((t) => <SelectItem key={t} value={t}>{t} · {TIPO_LABEL[t]}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      {account && (
        <span className={`font-mono text-[10px] px-1.5 py-0.5 border ${needsEq ? "border-amber-400 text-amber-700 bg-amber-50" : "text-primary border-primary/30 bg-primary/5"}`}
          title={needsEq ? "Cuenta por equipo: se recomienda indicar el equipo (opcional)" : ""}
          data-testid={`${testid}-code`}>{code}{needsEq ? " · equipo sugerido" : ""}</span>
      )}
    </div>
  );
}

/** Read-only chip for lists/details. */
export function CostCodeChip({ cc, testid }) {
  if (!cc?.code) return <span className="text-[10px] text-muted-foreground" data-testid={testid}>Sin cuenta</span>;
  return (
    <span className="inline-flex items-center gap-1 font-mono text-[10px]" title={cc.concepto} data-testid={testid}>
      <span className={`px-1 text-[9px] font-black ${TIPO_CLASS[cc.tipo] || ""}`}>{cc.tipo}</span>{cc.code}
    </span>
  );
}
